/* ============================================================================
  JMT INIT v3 (UTILITY-ONLY, Non-Competing)
  - NO preference capture logic (no change listeners added here)
  - Bootstrap-only: ensures defaults are set once on load if prefs are empty
  - Keeps: Free shipping banner logic + safe header offset

  FREE-SHIPPING BANNER RULES (2026-04-12)
  - Show ONLY for US visitors
  - If location cannot be determined, DO NOT show
  - Homepage: full banner behavior
  - Non-home pages: compact single-line banner
      "Add X more item(s) for free shipping"
      "Free shipping unlocked"

  Shopify location source:
  - Uses Shopify Customer Privacy API getRegion()
  - Country code = first 2 chars of returned region string
  - Empty string means unknown location
============================================================================ */

/* ============================================================================
  JMT INIT: Free-shipping banner logic + safe header offset
============================================================================ */
(function(){
  'use strict';

  var THRESHOLD = 3;
  var HOME_PAGE_TYPES = ['index'];

  function ready(fn){
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn)
      : fn();
  }

  function findHeader(){
    return document.querySelector('sticky-header')
        || document.querySelector('.header-wrapper')
        || document.querySelector('#shopify-section-header')
        || document.querySelector('.shopify-section-header')
        || document.querySelector('header[role="banner"]')
        || document.querySelector('header.site-header')
        || document.querySelector('header');
  }

  function getPageType(){
    var body = document.body;
    if (!body) return '';

    if (body.classList.contains('template-index')) return 'index';
    if (body.classList.contains('template-home')) return 'index';

    var path = (window.location && window.location.pathname) ? window.location.pathname : '/';
    if (path === '/' || path === '') return 'index';

    return 'other';
  }

  function isHomePage(){
    return HOME_PAGE_TYPES.indexOf(getPageType()) !== -1;
  }

  function setFsHeight(px){
    document.documentElement.style.setProperty('--jmt-fsbar-h', px + 'px');
  }

  function clearFsOffset(){
    setFsHeight(0);
    document.body.classList.remove('jmt-fs-on');

    var header = findHeader();
    if (header) header.classList.remove('jmt-offset-fixed-header');
  }

  function applyFsOffset(){
    var bar = document.getElementById('jmt-fsbar');
    var inner = document.getElementById('jmt-fsbar-inner');
    if (!bar || !inner || bar.hasAttribute('hidden')) {
      clearFsOffset();
      return;
    }

    var h = inner.offsetHeight || 0;
    setFsHeight(h);
    document.body.classList.add('jmt-fs-on');

    var header = findHeader();
    if (header && getComputedStyle(header).position === 'fixed'){
      header.classList.add('jmt-offset-fixed-header');
    }
  }

  function ensureCompactClass(bar){
    if (!bar) return;
    if (isHomePage()) bar.classList.remove('jmt-fsbar--compact');
    else bar.classList.add('jmt-fsbar--compact');
  }

  function insertBar(){
    if (document.getElementById('jmt-fsbar')) return;

    var bar = document.createElement('div');
    bar.id = 'jmt-fsbar';
    bar.setAttribute('hidden', '');

    var inner = document.createElement('div');
    inner.id = 'jmt-fsbar-inner';

    var wrap = document.createElement('div');
    wrap.className = 'jmt-textwrap';

    var l1 = document.createElement('span');
    l1.className = 'jmt-line1';
    l1.textContent = 'Buy ' + THRESHOLD + '+ items — Free shipping.';

    var l2 = document.createElement('span');
    l2.className = 'jmt-line2';
    l2.textContent = '';

    wrap.append(l1, l2);

    var close = document.createElement('button');
    close.className = 'jmt-close';
    close.setAttribute('aria-label','Hide message');
    close.textContent = '×';
    close.addEventListener('click', function(){
      bar.setAttribute('hidden','');
      clearFsOffset();
    });

    inner.append(wrap, close);
    bar.appendChild(inner);
    document.body.insertBefore(bar, document.body.firstChild);

    ensureCompactClass(bar);
  }

  function setBarVisible(shouldShow){
    var bar = document.getElementById('jmt-fsbar');
    if (!bar) return;

    if (shouldShow){
      bar.removeAttribute('hidden');
      applyFsOffset();
    } else {
      bar.setAttribute('hidden', '');
      clearFsOffset();
    }
  }

  function updateBar(count){
    var bar = document.getElementById('jmt-fsbar');
    if (!bar) return;

    var l1 = bar.querySelector('.jmt-line1');
    var l2 = bar.querySelector('.jmt-line2');
    var itemCount = count || 0;
    var left = Math.max(THRESHOLD - itemCount, 0);
    var state = 'default';
    var compact = !isHomePage();

    ensureCompactClass(bar);

    if (l1){
      l1.textContent = 'Buy ' + THRESHOLD + '+ items — Free shipping.';
    }

    if (itemCount >= THRESHOLD){
      if (l2){
        l2.textContent = compact
          ? 'Free shipping unlocked'
          : 'Free shipping unlocked — no code needed.';
      }
      state = 'unlocked';
    } else {
      if (l2){
        if (compact){
          l2.textContent = 'Add ' + left + ' more ' + (left === 1 ? 'item' : 'items') + ' for free shipping';
        } else {
          l2.textContent = 'Add ' + left + ' more ' + (left === 1 ? 'item' : 'items') + ' to unlock.';
        }
      }

      if (left === 1) state = 'near';
      else if (itemCount === 1) state = 'progress';
    }

    bar.setAttribute('data-state', state);

    if (!bar.hasAttribute('hidden')){
      applyFsOffset();
    }
  }

  function fetchCart(){
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { credentials:'same-origin', cache:'no-store' })
      .then(function(r){ return r.json(); });
  }

  function hookCartUpdates(){
    document.addEventListener('submit', function(e){
      var f = e.target;
      if (!(f instanceof HTMLFormElement)) return;

      var action = (f.getAttribute('action') || '');
      if (/\/cart\/add(\.js)?/i.test(action)){
        setTimeout(function(){
          fetchCart().then(function(c){
            updateBar(c.item_count || 0);
          }).catch(function(){});
        }, 400);
      }
    }, true);

    if (window.fetch){
      var _fetch = window.fetch;
      window.fetch = function(){
        var args = arguments;
        var url = args[0] && args[0].toString ? args[0].toString() : '';
        var p = _fetch.apply(this, args);

        if (/\/cart\/(add|change|update)(\.js)?/i.test(url)){
          p.then(function(res){
            if (res && res.ok){
              setTimeout(function(){
                fetchCart().then(function(c){
                  updateBar(c.item_count || 0);
                }).catch(function(){});
              }, 400);
            }
          }).catch(function(){});
        }

        return p;
      };
    }
  }

  function extractCountryFromRegion(region){
    if (!region || typeof region !== 'string') return '';
    return region.slice(0, 2).toUpperCase();
  }

  function getRegionFromPrivacyAPI(){
    try{
      if (
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.getRegion === 'function'
      ){
        return window.Shopify.customerPrivacy.getRegion() || '';
      }
    }catch(_){}
    return '';
  }

  function resolveCountryWithPrivacyAPI(callback){
    function finish(region){
      callback(extractCountryFromRegion(region));
    }

    if (
      window.Shopify &&
      window.Shopify.customerPrivacy &&
      typeof window.Shopify.customerPrivacy.getRegion === 'function'
    ){
      finish(getRegionFromPrivacyAPI());
      return;
    }

    if (
      window.Shopify &&
      typeof window.Shopify.loadFeatures === 'function'
    ){
      try{
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function(error){
            if (error){
              finish('');
              return;
            }
            finish(getRegionFromPrivacyAPI());
          }
        );
        return;
      }catch(_){}
    }

    finish('');
  }

  function initFreeShippingBar(){
    insertBar();
    var initialBar = document.getElementById('jmt-fsbar');
    if (initialBar && !initialBar.hasAttribute('hidden')){
      applyFsOffset();
    } else {
      clearFsOffset();
    }

    resolveCountryWithPrivacyAPI(function(country){
      if (country !== 'US'){
        setBarVisible(false);
        return;
      }

      setBarVisible(true);

      fetchCart()
        .then(function(c){
          updateBar(c.item_count || 0);
        })
        .catch(function(){
          updateBar(0);
        });
    });

    window.addEventListener('resize', function(){
      var bar = document.getElementById('jmt-fsbar');
      if (!bar || bar.hasAttribute('hidden')) return;
      ensureCompactClass(bar);
      applyFsOffset();
    });

    hookCartUpdates();
  }

  ready(function(){
    initFreeShippingBar();
  });
})();


/* ============================================================================
  JMT PREF BOOTSTRAP (Non-competing, no change capture)

  NEW RULE:
    - NEVER seed prefs from PDP variant defaults.
    - NEVER seed prefs by scanning document selectors.
    - ONLY seed from the JMT preference bar (#jmt-prefbar),
      and ONLY when it has a real (non-placeholder) selection.

  Outcome:
    - First-time visitor must explicitly choose cut/size on collection/search
      preference bar before Quick Add unlocks.
    - Visiting a PDP can no longer "accidentally" create prefs.
============================================================================ */
(function(){
  'use strict';

  var PREF_EVT = 'jmt:pref:change';

  function ready(fn){
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn)
      : fn();
  }

  function norm(s){ return String(s || '').toLowerCase().trim(); }

  function isProductPage(){
    try{
      if (document.body && (document.body.classList.contains('template-product') || document.body.classList.contains('template-product__main'))) return true;
    }catch(_){}
    if (location && typeof location.pathname === 'string' && location.pathname.indexOf('/products/') !== -1) return true;
    if (document.querySelector('product-info') || document.querySelector('product-form')) return true;
    return false;
  }

  function hasPrefAPI(){
    return !!(window.JMT_PREF
      && typeof window.JMT_PREF.get === 'function'
      && typeof window.JMT_PREF.set === 'function');
  }

  function getPrefSafe(){
    if (!hasPrefAPI()) return {};
    try { return window.JMT_PREF.get() || {}; } catch(e){ return {}; }
  }

  function emitPref(pref){
    try { window.dispatchEvent(new CustomEvent(PREF_EVT, { detail: pref })); } catch(e){}
  }

  function setPrefOnce(next){
    if (!hasPrefAPI()) return false;

    var cur = getPrefSafe();
    var needsCut  = !cur.cut  && !!next.cut;
    var needsSize = !cur.size && !!next.size;

    if (!needsCut && !needsSize) return false;

    var merged = {
      cut:  needsCut  ? next.cut  : (cur.cut  || ''),
      size: needsSize ? next.size : (cur.size || '')
    };

    /* JMT_PREF.set emits the complete canonical state. Do not emit a second
       cut/size-only event, because that would temporarily clear saved fit
       adjustments in other synchronized controls. */
    try { window.JMT_PREF.set(merged); } catch(e){ return false; }
    return true;
  }

  function getSelectValue(sel){
    if (!sel) return '';
    return String(sel.value || '').trim();
  }

  function isPlaceholder(v){
    v = norm(v);
    return !v || v === 'choose' || v === 'select' || v === 'choose…' || v === 'choose...' || v === 'select…' || v === 'select...';
  }

  function readFromPrefBar(){
    var prefbar = document.getElementById('jmt-prefbar') || document.querySelector('.jmt-prefbar');
    if (!prefbar) return { cut:'', size:'' };

    var cutSel =
      prefbar.querySelector('#jmt-prefbar-cut') ||
      prefbar.querySelector('select[name*="cut" i]') ||
      prefbar.querySelector('select[id*="cut" i]') ||
      prefbar.querySelector('select');

    var sizeSel =
      prefbar.querySelector('#jmt-prefbar-size') ||
      prefbar.querySelector('select[name*="size" i]') ||
      prefbar.querySelector('select[id*="size" i]') ||
      (function(){
        var sels = prefbar.querySelectorAll('select');
        return sels && sels.length > 1 ? sels[1] : null;
      })();

    var cut = getSelectValue(cutSel);
    var size = getSelectValue(sizeSel);

    if (isPlaceholder(cut)) cut = '';
    if (isPlaceholder(size)) size = '';

    return { cut: cut, size: size };
  }

  function bootstrap(){
    if (!hasPrefAPI()) return;
    if (isProductPage()) return;

    var found = readFromPrefBar();
    if (!found.cut || !found.size) return;

    setPrefOnce({ cut: found.cut, size: found.size });
  }

  ready(function(){
    bootstrap();
    setTimeout(bootstrap, 150);
    setTimeout(bootstrap, 600);

    document.addEventListener('shopify:section:load', bootstrap);
  });
})();

/* ============================================================================
  JMT FIT COMPARISON + CLEAN LINE-ITEM PROPERTIES
  - Adds a non-variant "Compare the differences" choice to Pouch Style and Thong Strap Style.
  - Restores the real selection before any variant-selection listener runs.
  - Opens the Fit Builder so customers can compare the available styles.
  - Omits blank adjustment properties from add-to-cart requests.
============================================================================ */
(function(){
  'use strict';

  var POUCH_GUIDE_VALUE = '__JMT_POUCH_GUIDE__';
  var STRAP_GUIDE_VALUE = '__JMT_STRAP_GUIDE__';
  var POUCH_GUIDE_URL = 'https://cdn.shopify.com/s/files/1/0617/2613/0259/files/JMT_Fit_Guide_V2.jpg?v=1775269138';
  var STRAP_GUIDE_URL = 'https://cdn.shopify.com/s/files/1/0617/2613/0259/files/Thong_Strap_Style_Infographic_JMT.jpg?v=1784540970';
  var guideOriginSelect = null;

  function optionKind(select){
    if (!select) return '';
    var label = select.getAttribute('data-jmt-option-name') || select.getAttribute('aria-label') || select.name || select.id || '';
    var labelledBy = select.getAttribute('aria-labelledby');
    if (labelledBy) {
      var labelNode = document.getElementById(labelledBy);
      if (labelNode) label += ' ' + labelNode.textContent;
    }
    var wrap = select.closest('label, .product-form__input, .jmt-sticky__option, .jmt-prefbar__option, .jmt-cart-editor__field');
    if (wrap) label += ' ' + (wrap.textContent || '').slice(0, 100);
    label = String(label).toLowerCase();
    if (label.indexOf('thong strap style') !== -1 || label.indexOf('strap style') !== -1 || label.indexOf('jmt-prefbar-strap') !== -1) return 'strap';
    if (label.indexOf('pouch style') !== -1 || label.indexOf('choose your cut') !== -1 || label.indexOf('jmt-prefbar-cut') !== -1) return 'pouch';
    return '';
  }

  function ensureGuideOption(select){
    if (select && select.closest && select.closest('#jmt-fit-builder')) return;
    var kind = optionKind(select);
    if (!kind) return;
    var value = kind === 'pouch' ? POUCH_GUIDE_VALUE : STRAP_GUIDE_VALUE;
    if (select.querySelector('option[value="' + value + '"]')) return;
    var option = document.createElement('option');
    option.value = value;
    option.textContent = 'Compare the differences';
    option.setAttribute('data-jmt-guide-option', kind);
    select.appendChild(option);
    if (select.value !== value) select.dataset.jmtLastRealValue = select.value || '';
  }

  function scan(root){
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches('select')) ensureGuideOption(scope);
    scope.querySelectorAll('select').forEach(ensureGuideOption);
  }

  function openFitComparison(kind, originSelect){
    guideOriginSelect = originSelect || null;
    var attempts = 0;

    function launch(){
      if (window.JMT_FIT_BUILDER && typeof window.JMT_FIT_BUILDER.open === 'function') {
        window.JMT_FIT_BUILDER.open('variant_' + kind + '_comparison');
        return;
      }
      attempts += 1;
      if (attempts < 20) window.setTimeout(launch, 50);
    }

    launch();
  }

  function closeGuide(returnToSelector){
    var overlay = document.getElementById('jmt-option-guide-overlay');
    if (overlay) overlay.hidden = true;
    document.documentElement.style.overflow = '';
    if (returnToSelector && guideOriginSelect && document.contains(guideOriginSelect)) {
      guideOriginSelect.focus({ preventScroll: true });
      if (typeof guideOriginSelect.showPicker === 'function') {
        try { guideOriginSelect.showPicker(); } catch (error) {}
      }
    }
    if (!returnToSelector) guideOriginSelect = null;
  }

  function openGuide(kind, originSelect){
    guideOriginSelect = originSelect || null;
    var overlay = document.getElementById('jmt-option-guide-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'jmt-option-guide-overlay';
      overlay.hidden = true;
      overlay.innerHTML = '<div class="jmt-option-guide__dialog" role="dialog" aria-modal="true" aria-labelledby="jmt-option-guide-title">' +
        '<button type="button" class="jmt-option-guide__close" aria-label="Close guide">&times;</button>' +
        '<h2 id="jmt-option-guide-title" class="jmt-option-guide__title"></h2>' +
        '<img class="jmt-option-guide__image" alt="">' +
        '</div>';
      var style = document.createElement('style');
      style.textContent = '#jmt-option-guide-overlay{position:fixed;inset:0;z-index:1000000;display:flex;align-items:center;justify-content:center;padding:calc(var(--jmt-fsbar-h,0px) + 1.6rem) 1.6rem 1.6rem;background:rgba(0,0,0,.62)}#jmt-option-guide-overlay[hidden]{display:none!important}.jmt-option-guide__dialog{position:relative;width:min(92vw,760px);max-height:calc(100dvh - var(--jmt-fsbar-h,0px) - 3.2rem);padding:4.8rem 1.2rem 1.2rem;background:rgb(var(--color-background,255 255 255));border:.1rem solid rgba(var(--color-foreground,0 0 0),.2);border-radius:var(--buttons-radius,0);box-shadow:0 1.2rem 4rem rgba(0,0,0,.22);overflow:auto;font-family:var(--font-body-family,inherit);color:rgb(var(--color-foreground,0 0 0))}.jmt-option-guide__title{margin:0 4rem 1.2rem 0;text-align:center;font-family:var(--font-heading-family,var(--font-body-family,inherit));font-size:clamp(1.8rem,3vw,2.6rem)}.jmt-option-guide__image{display:block;width:100%;max-height:calc(100dvh - var(--jmt-fsbar-h,0px) - 11rem);object-fit:contain}.jmt-option-guide__close{position:absolute;top:1rem;right:1rem;width:3.8rem;height:3.8rem;padding:0;border:.1rem solid rgba(var(--color-foreground,0 0 0),.35);border-radius:50%;background:rgb(var(--color-background,255 255 255));color:inherit;font:inherit;font-size:2.6rem;line-height:1;cursor:pointer}';
      style.textContent += '.jmt-option-guide__dialog{font-style:var(--font-body-style,normal);font-weight:var(--font-body-weight,400);letter-spacing:.06rem}.jmt-option-guide__title{font-style:var(--font-heading-style,normal);font-weight:var(--font-heading-weight,400);letter-spacing:calc(var(--font-heading-scale,1) * .06rem)}';
      document.head.appendChild(style);
      document.body.appendChild(overlay);
      overlay.querySelector('.jmt-option-guide__close').addEventListener('click', function(){ closeGuide(true); });
      overlay.addEventListener('click', function(event){ if (event.target === overlay) closeGuide(false); });
    }
    var isPouch = kind === 'pouch';
    overlay.querySelector('.jmt-option-guide__title').textContent = isPouch ? 'Pouch Style Guide' : 'Thong Strap Style Guide';
    var image = overlay.querySelector('.jmt-option-guide__image');
    image.src = isPouch ? POUCH_GUIDE_URL : STRAP_GUIDE_URL;
    image.alt = isPouch ? 'Just Mens Thongs pouch style fit guide.' : 'Thong strap style infographic for Just Mens Thongs.';
    overlay.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    overlay.querySelector('.jmt-option-guide__close').focus();
  }

  document.addEventListener('focusin', function(event){
    var select = event.target && event.target.matches && event.target.matches('select') ? event.target : null;
    if (select && select.value !== POUCH_GUIDE_VALUE && select.value !== STRAP_GUIDE_VALUE) select.dataset.jmtLastRealValue = select.value || '';
  }, true);

  document.addEventListener('change', function(event){
    var select = event.target && event.target.matches && event.target.matches('select') ? event.target : null;
    if (!select) return;
    var kind = select.value === POUCH_GUIDE_VALUE ? 'pouch' : (select.value === STRAP_GUIDE_VALUE ? 'strap' : '');
    if (!kind) {
      if (optionKind(select)) select.dataset.jmtLastRealValue = select.value || '';
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    var fallbackOption = Array.from(select.options).find(function(opt){
      return !opt.hasAttribute('data-jmt-guide-option') && opt.value;
    });
    select.value = select.dataset.jmtLastRealValue || (fallbackOption ? fallbackOption.value : '') || '';
    openFitComparison(kind, select);
  }, true);

  document.addEventListener('keydown', function(event){
    if (event.key === 'Escape') closeGuide(false);
  });

  document.addEventListener('submit', function(event){
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add/i.test(form.action || '')) return;
    var blanks = Array.from(document.querySelectorAll('[name^="properties["]')).filter(function(field){
      return field.form === form && String(field.value || '').trim() === '';
    });
    blanks.forEach(function(field){ field.disabled = true; });
    window.setTimeout(function(){ blanks.forEach(function(field){ field.disabled = false; }); }, 0);
  }, true);

  function boot(){
    scan(document);
    if (!document.body) return;
    new MutationObserver(function(records){
      records.forEach(function(record){ record.addedNodes.forEach(function(node){ if (node.nodeType === 1) scan(node); }); });
    }).observe(document.body,{childList:true,subtree:true});
  }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded',boot) : boot();
})();


/* JMT unified storefront enhancements v3 */
(function(){
  'use strict';
  function makeSupportCard(){
    var card=document.createElement('section');
    card.className='jmt-support-guide';
    card.id='jmt-support-guide';
    card.setAttribute('aria-labelledby','jmt-support-guide-title');
    card.innerHTML='<p class="jmt-support-guide__eyebrow">Before you email</p><h2 id="jmt-support-guide-title">The quickest answer may already be waiting.</h2><p>Our FAQ covers sizing, processing, shipping, returns, and restocks.</p><details class="jmt-support-guide__delivery"><summary>Where can I find my estimated delivery date?</summary><p>Check the estimated delivery date in your confirmation email for the most accurate timeframe for your order. If you email us asking for the timeframe, we can only direct you back to that confirmation.</p></details><div class="jmt-support-guide__actions"><a href="/pages/faq">Check the FAQ</a><a href="mailto:support@justmensthongs.com">Email support</a></div>';
    return card;
  }
  function initSupportPages(){
    var path=location.pathname.replace(/\/$/,'');
    if(path==='/pages/contact-us'){
      document.body.classList.add('jmt-contact-page');
      if(!document.getElementById('jmt-support-guide')){
        var pageSection=document.querySelector('main .shopify-section');
        if(pageSection) pageSection.insertAdjacentElement('afterend',makeSupportCard());
      }
    }
if(path==='/policies/contact-information'){
  document.body.classList.add('jmt-contact-policy-page');
  var policyBody=document.querySelector('.shopify-policy__body');
  if(policyBody&&!document.getElementById('jmt-policy-delivery-estimate')){
    var delivery=document.createElement('details');
    delivery.id='jmt-policy-delivery-estimate';
    delivery.className='jmt-policy-delivery-estimate';
    delivery.innerHTML='<summary><span>Check your confirmation email for your delivery date estimate</span><span aria-hidden="true">+</span></summary><div class="jmt-policy-delivery-estimate__answer"><p>Your order confirmation email contains the most accurate estimated delivery date for your specific order. If you contact us for a delivery timeframe, we can only direct you back to that confirmation email.</p></div>';
    policyBody.appendChild(delivery);
    delivery.addEventListener('toggle',function(){var mark=delivery.querySelector('summary [aria-hidden]');if(mark)mark.textContent=delivery.open?'−':'+';});
  }
}
if(path==='/policies/shipping-policy'){
  var shippingPolicyRoot=document.querySelector('.shopify-policy__body > div');
  if(shippingPolicyRoot&&!shippingPolicyRoot.dataset.jmtAccordionReady){
    shippingPolicyRoot.dataset.jmtAccordionReady='true';
    Array.from(shippingPolicyRoot.children).forEach(function(heading){
      if(heading.tagName!=='P') return;
      var titleNode=heading.querySelector(':scope > b:first-child, :scope > strong:first-child');
      if(!titleNode||!/^\s*\d+\./.test(titleNode.textContent||'')) return;

      var followingList=heading.nextElementSibling;
      var details=document.createElement('details');
      details.className='jmt-policy-accordion';

      var summary=document.createElement('summary');
      var summaryLabel=document.createElement('span');
      summaryLabel.textContent=(titleNode.textContent||'').trim();
      var summaryMark=document.createElement('span');
      summaryMark.setAttribute('aria-hidden','true');
      summaryMark.textContent='+';
      summary.appendChild(summaryLabel);
      summary.appendChild(summaryMark);

      var answer=document.createElement('div');
      answer.className='jmt-policy-accordion__answer';
      var lead=heading.cloneNode(true);
      var leadTitle=lead.querySelector(':scope > b:first-child, :scope > strong:first-child');
      if(leadTitle) leadTitle.remove();
      if((lead.textContent||'').trim()) answer.appendChild(lead);

      shippingPolicyRoot.insertBefore(details,heading);
      details.appendChild(summary);
      details.appendChild(answer);
      heading.remove();

      if(followingList&&/^(UL|OL)$/.test(followingList.tagName)){
        answer.appendChild(followingList);
      }

      details.addEventListener('toggle',function(){
        summaryMark.textContent=details.open?'−':'+';
      });
    });
  }
}
if(path==='/pages/faq'){
      document.body.classList.add('jmt-faq-page');
      var list=document.querySelector('.jmt-faq-list');
      if(list&&!document.getElementById('jmt-delivery-date-faq')){
        var item=document.createElement('details');
        item.className='jmt-faq-item';
        item.id='jmt-delivery-date-faq';
        item.innerHTML='<summary class="jmt-faq-item__question">Where can I find my estimated delivery date?</summary><div class="jmt-faq-item__answer"><p>Your most accurate estimated delivery date is included in your order confirmation email. It is calculated for your specific order. If you email us asking for a delivery timeframe, we can only direct you back to that confirmation email.</p></div>';
        list.appendChild(item);
        var button=item.querySelector('.jmt-faq-item__question');
        var answer=item.querySelector('.jmt-faq-item__answer');
        button.addEventListener('jmt-noop',function(){
          var open=button.getAttribute('aria-expanded')==='true';
          button.setAttribute('aria-expanded',open?'false':'true');
          answer.hidden=open;
          var mark=button.querySelector('[aria-hidden]');
          if(mark) mark.textContent=open?'+':'−';
        });
      }
    }
  }
  function init(){initSupportPages();}
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',init); else init();
  document.addEventListener('shopify:section:load',init);
})();

/* JMT: hydrate Judge.me review photos when its lazy loader leaves data-src unused */
(function(){
  function enhanceJudgeMeWriteReview(root){
    var selector='#judgeme_product_reviews .jdgm-write-rev-link';
    var scope=root&&root.querySelectorAll?root:document;
    var links=[];

    if(scope.matches&&scope.matches(selector)) links=[scope];
    else links=scope.querySelectorAll(selector);

    links.forEach(function(link){
      if(link.dataset.jmtKeyboardBound) return;
      link.dataset.jmtKeyboardBound='true';
      link.addEventListener('keydown',function(event){
        if(event.key!=='Enter'&&event.key!==' ') return;
        event.preventDefault();
        link.click();
      });
    });
  }

  function hydrateJudgeMeReviewImages(root){
    var scope=root&&root.querySelectorAll?root:document;
    var images=scope.querySelectorAll('#judgeme_product_reviews img.jdgm-rev__pic-img[data-src]');

    images.forEach(function(image){
      if(!image.getAttribute('src')&&image.dataset.src){
        image.setAttribute('src',image.dataset.src);
        image.setAttribute('loading','lazy');
      }

      if(!image.dataset.jmtLoadBound){
        image.dataset.jmtLoadBound='true';
        image.addEventListener('load',function(){
          var link=image.closest('.jdgm-rev__pic-link');
          if(link) link.classList.remove('jdgm--loading');
        },{once:true});
      }
    });
  }

  function initJudgeMeReviewImages(){
    hydrateJudgeMeReviewImages(document);
    enhanceJudgeMeWriteReview(document);

    if(!('MutationObserver' in window)) return;
    var observer=new MutationObserver(function(mutations){
      mutations.forEach(function(mutation){
        mutation.addedNodes.forEach(function(node){
          if(node.nodeType!==1) return;
          enhanceJudgeMeWriteReview(node);
          if(node.matches&&node.matches('#judgeme_product_reviews img.jdgm-rev__pic-img[data-src]')){
            hydrateJudgeMeReviewImages(node.parentElement||document);
          }else if(node.querySelector&&node.querySelector('#judgeme_product_reviews img.jdgm-rev__pic-img[data-src]')){
            hydrateJudgeMeReviewImages(node);
          }
        });
      });
    });
    observer.observe(document.documentElement,{childList:true,subtree:true});
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',initJudgeMeReviewImages);
  }else{
    initJudgeMeReviewImages();
  }
})();

/* Keep policy-page email addresses usable when Shopify renders them as links without hrefs. */
(function(){
  function hydratePolicyEmailLinks(root){
    var scope=root&&root.querySelectorAll?root:document;
    scope.querySelectorAll('a:not([href])').forEach(function(link){
      var address=(link.textContent||'').trim();
      if(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)){
        link.href='mailto:'+address;
      }
    });
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',function(){hydratePolicyEmailLinks(document);});
  }else{
    hydratePolicyEmailLinks(document);
  }
  document.addEventListener('shopify:section:load',function(event){hydratePolicyEmailLinks(event.target);});
})();



